import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Draggable from 'react-draggable';
import {
  useMediaQuery,
  Card,
  CardContent,
  Typography,
  CardActions,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Badge,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import ViewIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import MapIcon from '@mui/icons-material/Map';
import SensorsIcon from '@mui/icons-material/Sensors';
import AddGeofenceIcon from '@mui/icons-material/AddLocation';
import EditGeofencesIcon from '@mui/icons-material/EditLocationAlt';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import ConstructionOutlinedIcon from '@mui/icons-material/ConstructionOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import SpeedIcon from '@mui/icons-material/Speed';
import SendIcon from '@mui/icons-material/Send';
import { PauseCircleFilled as StopCircleIcon } from '@mui/icons-material';
import OfflineBoltIcon from '@mui/icons-material/OfflineBolt';
import CircleNotificationsIcon from '@mui/icons-material/CircleNotifications';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import BuildIcon from '@mui/icons-material/Build';
import TroubleshootIcon from '@mui/icons-material/Troubleshoot';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import FaceRetouchingNaturalIcon from '@mui/icons-material/FaceRetouchingNatural';
import { useTheme } from '@mui/material/styles';

import { useTranslation } from './LocalizationProvider';
import TerminateDialog from './TerminateDialog';
import {
  useAdministrator, useRecovery, useCommandSend, useRecoveryAssign, useDeviceManage, useAlertsManage, useDeviceEdit,
} from '../util/permissions';
import { devicesActions } from '../../store';
import { getUserCommandId } from './CommonValues';
import {
  callJsonAPI,
  postJsonAPI,
} from '../util/utils';
import fetchMsgPack from '../util/apiUtils';
import useLiveTrack from '../util/useLiveTrack';
import useAgentLocation, { distanceMeters, formatNearDistance } from '../util/useAgentLocation';
import { useAttributePreference } from '../util/preferences';
import MaintenanceDialog from './MaintenanceDialog';
import SpeedLimitDialog from './SpeedLimitDialog';
import GeofenceDialog from './GeofenceDialog';
import GeofenceListDialog from './GeofenceListDialog';
import NotificationsDialog from './NotificationsDialog';
import TransferDialog from './TransferDialog';
import RecoveryDialog from './RecoveryDialog';
import PropsDialog from './PropsDialog';
import TabPanel from './StatusTabPanel';
import { ActionIcon } from './ActionObject';
import { MessageDisplay, getLotStatusDetails } from './CommonObjects';
import { getLotStatus } from '../../main/filterUtil';
import LotInfoDialog from './LotInfoDialog';
import CopyableText from './CopyableText';
import StandardProps from './StandardProps';
import AIAnalysisDialog from './AIAnalysisDialog';
import { MapMenuItem } from './MapElement';

import { ReactComponent as RecoveryIcon } from '../../resources/images/data/recovery.svg';

const useStyles = makeStyles((theme) => ({
  card: {
    [theme.breakpoints.up('lg')]: {
      width: theme.dimensions.popupMaxWidthLargeScreen,
    },
    [theme.breakpoints.down('lg')]: {
      width: theme.dimensions.popupMaxWidthSmallScreen,
    },
  },
  media: {
    height: 100,
    display: 'block',
  },
  mediaButton: {
    color: theme.palette.colors.white,
    mixBlendMode: 'difference',
  },
  header: {
    display: 'flex',
    padding: theme.spacing(1, 1, 0, 2),
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.up('md')]: {
      flex: '1 1 30%',
    },
    [theme.breakpoints.down('md')]: {
      order: 2,
      marginTop: theme.spacing(0.5),
      width: '100%',
    },
  },
  headerMiddle: {
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.up('md')]: {
      justifyContent: 'center',
      flex: '1 1 70%',
    },
    [theme.breakpoints.down('md')]: {
      order: 1,
      justifyContent: 'flex-start',
      width: 'calc(100% - 35px)',
    },
  },
  headerRight: {
    display: 'flex',
    justifyContent: 'flex-end',
    [theme.breakpoints.up('md')]: {
      flex: '1 1 30%',
    },
    [theme.breakpoints.down('md')]: {
      position: 'absolute',
      top: theme.spacing(0.5),
      right: theme.spacing(1),
    },
  },
  statusIconsContainer: {
    '& .MuiIconButton-root': {
      padding: 2,
    },
    '& svg': {
      width: 18,
      height: 18,
    },
  },
  content: {
    paddingBottom: theme.spacing(1),
    paddingTop: theme.spacing(0.5),
    [theme.breakpoints.up('md')]: {
      paddingTop: theme.spacing(0),
    },
    [theme.breakpoints.down('md')]: {
      paddingTop: theme.spacing(0.5),
    },
  },
  negative: {
    color: theme.palette.colors.negative,
  },
  medium: {
    color: theme.palette.colors.medium,
  },
  icon: {
    width: '25px',
    height: '25px',
    filter: 'brightness(0) invert(1)',
  },
  root: ({ desktopPadding }) => ({
    position: 'fixed',
    zIndex: 5,
    left: '50%',
    [theme.breakpoints.up('md')]: {
      left: `calc(50% + ${desktopPadding} / 2)`,
      bottom: `calc(${theme.spacing(3)} + var(--safe-area-inset-bottom))`,
    },
    [theme.breakpoints.down('md')]: {
      left: '50%',
      bottom: `calc(${theme.spacing(3)} + ${theme.dimensions.bottomBarHeight}px + var(--safe-area-inset-bottom))`,
    },
    transform: 'translateX(-50%)',
  }),
}));

const StatusCard = ({ deviceId, position, onClose, disableActions, desktopPadding = 0 }) => {
  // const startTime = performance.now();
  const classes = useStyles({ desktopPadding });
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const t = useTranslation();
  const theme = useTheme();

  const admin = useAdministrator();
  const recovery = useRecovery();
  const canEditDevice = useDeviceEdit();
  const canSendCommands = useCommandSend();
  const canAssignRecovery = useRecoveryAssign();
  const canManageDevices = useDeviceManage();
  const canManageAlerts = useAlertsManage();

  const device = useSelector((state) => state.devices.items[deviceId]);
  const group = useSelector((state) => state.groups.items[device?.groupId]);
  const userId = useSelector((state) => state.session.user.id);
  const userEmail = useSelector((state) => state.session.user.email);

  const [anchorMap, setAnchorMap] = useState(null);
  const [anchorMore, setAnchorMore] = useState(null);
  const [anchorAlerts, setAnchorAlerts] = useState(null);
  const [anchorActions, setAnchorActions] = useState(null);
  const [anchorDevice, setAnchorDevice] = useState(null);
  const [anchorCommands, setAnchorCommands] = useState(null);
  const [anchorAdminCommands, setAnchorAdminCommands] = useState(null);
  const [adminCommandsPosition, setAdminCommandsPosition] = useState(null);

  const [deviceCommands, setDeviceCommands] = useState([]);
  const [adminCommands, setAdminCommands] = useState([]);

  const [openRemoveDialog, setOpenRemoveDialog] = useState(false);
  const [opentTerminateDialog, setOpentTerminateDialog] = useState(false);
  const [opentResetDialog, setOpentResetDialog] = useState(false);

  const [openRecoveryDialog, setOpenRecoveryDialog] = React.useState(false);
  const [openMaintenanceDialog, setOpenMaintenanceDialog] = React.useState(false);
  const [openSpeedLimitDialog, setOpenSpeedLimitDialog] = React.useState(false);
  const [openGeofenceDialog, setOpenGeofenceDialog] = React.useState(false);
  const [openGeofenceListDialog, setOpenGeofenceListDialog] = React.useState(false);
  const [openTransferDialog, setOpenTransferDialog] = React.useState(false);
  const [openNotificationsDialog, setOpenNotificationsDialog] = React.useState(false);
  const [openPropsDialog, setOpenPropsDialog] = React.useState(false);
  const [openLotInfoDialog, setOpenLotInfoDialog] = React.useState(false);
  const [openAnalysisDialog, setOpenAnalysisDialog] = React.useState(false);
  const [actionResult, setActionResult] = useState();
  const [actionResultError, setActionResultError] = useState(false);

  const largeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const phone = useMediaQuery(theme.breakpoints.down('md'));

  const lotStatus = device ? getLotStatus(device) : null;
  const lotDetails = getLotStatusDetails(lotStatus);
  const LotIcon = lotDetails?.Icon;

  const setActionMessage = (message, isError = false) => {
    setActionResult(message);
    setActionResultError(isError);
  };

  const setSuccessMessage = (message) => {
    setActionMessage(message, false);
  };

  const setErrorMessage = (message) => {
    setActionMessage(message, true);
  };

  // Live tracking repeats the saved Refresh command. It is offered to anyone
  // with the command-send permission, so a lender can follow their own
  // repossession, but only on a vehicle flagged for recovery, which keeps the
  // extra traffic to vehicles already in that state, and only when the server
  // classes the unit as wired: a battery tracker would just drain, and it
  // usually cannot be reached between its own reports.
  const refreshCandidates = admin ? [...deviceCommands, ...adminCommands] : deviceCommands;
  const refreshLabel = (t('commandRefresh') || 'refresh').toLowerCase();
  const refreshCommand = refreshCandidates.find((command) => command.type === 'positionSingle')
    || refreshCandidates.find((command) => (command.description || '').toLowerCase() === refreshLabel);
  const liveTrack = useLiveTrack(deviceId, refreshCommand?.id, (message) => setActionMessage(message, true));
  const canLiveTrack = canSendCommands
    && device?.recovery && device?.deviceClass === 'wired' && !!refreshCommand;

  // While a session runs on a phone, its own position is watched so the
  // agent can see how far away the vehicle is. Straight-line distance, in
  // the user's unit; nothing is shown if the phone declines or has no fix.
  // A desktop never asks: its location is a rough network guess, usually an
  // office, and framing that with the vehicle only zooms the map out.
  const distanceUnit = useAttributePreference('distanceUnit');
  const { location: agentLocation } = useAgentLocation(liveTrack.active && phone);
  const agentDistance = agentLocation && position
    ? formatNearDistance(distanceMeters(agentLocation, position), distanceUnit)
    : null;

  // close card in ten minutes
  useEffect(() => {
    const interval = setInterval(() => {
      onClose();
    }, 10 * 60 * 1000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  // fetch commands for this device
  useEffect(() => {
    const fetchCommands = async () => {
      if (deviceId && canSendCommands) {
        try {
          const response = await fetch(`/api/commands/send?deviceId=${deviceId}`);
          console.log('Commands API response status:', response.status);
          if (response.ok) {
            const commands = await response.json();
            console.log('Commands from server for deviceId', deviceId, ':', commands);
            // Filter out backend scheduled commands (those starting with "UPDATE.")
            const visibleCommands = commands.filter((cmd) => !cmd.description?.startsWith('UPDATE.'));
            // Non-admin commands shown in main area (for all users)
            setDeviceCommands(visibleCommands.filter((cmd) => !cmd.adminOnly));
            // Admin commands shown in Actions menu (for admins only)
            if (admin) {
              setAdminCommands(visibleCommands.filter((cmd) => cmd.adminOnly));
            }
          }
        } catch (error) {
          console.error('Commands fetch error:', error);
          console.error('Stack trace:', error.stack);
        }
      }
    };
    fetchCommands();
  }, [deviceId, canSendCommands, admin]);

  const handleRefresh = async () => {
    const devices = await fetchMsgPack('/api/devices'); // Use the utility function
    dispatch(devicesActions.refresh(devices)); // Dispatch the decoded data
  };

  const handleRemove = async (device) => {
    setOpenRemoveDialog(false);
    if (device) {
      try {
        const response = await fetch(`/api/devices/${device.id}`, { method: 'DELETE' });
        if (response.ok) {
          setSuccessMessage(t('deviceRemoved'));
          onClose();
          handleRefresh();
        } else {
          const errorText = await response.text();
          try {
            const errorData = JSON.parse(errorText);
            setErrorMessage(errorData.error || errorData.message || response.statusText);
          } catch {
            setErrorMessage(response.statusText);
          }
        }
      } catch (error) {
        setErrorMessage(error.message);
      }
    }
  };

  // const displayMessage = (message) => {
  //   console.log(message);
  // };

  const handleTerminate = async (device) => {
    setOpentTerminateDialog(false);
    if (device) {
      try {
        const terminatedDevice = { ...device, disabled: true };
        const response = await fetch(`/api/devices/${device.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(terminatedDevice),
        });
        if (response.ok) {
          setSuccessMessage(t('deviceTerminated'));
          handleRefresh();
        } else {
          const errorText = await response.text();
          try {
            const errorData = JSON.parse(errorText);
            setErrorMessage(errorData.error || errorData.message || response.statusText);
          } catch {
            setErrorMessage(response.statusText);
          }
        }
      } catch (error) {
        setErrorMessage(error.message);
      }
    }
  };

  const getNotificationObject = (type, geofenceId) => ({
    type,
    geofenceId,
  });

  const createNotification = async (notification, deviceId) => {
    const body = { type: notification.type, geofenceId: notification.geofenceId, notificators: 'mail,sms,web', attributes: {}, always: false, commandId: 0 };
    const newNotification = await postJsonAPI('notifications', body, true, null, (msg) => setActionMessage(msg, true));
    if (newNotification) {
      await postJsonAPI('permissions', { deviceId, notificationId: newNotification.id }, false, null, (msg) => setActionMessage(msg, true));
    }
    return newNotification;
  };

  const deleteNotification = async (notificationId) => {
    await callJsonAPI(`notifications/${notificationId}`, 'DELETE', {}, false, null, (msg) => setActionMessage(msg, true));
  };

  const handleReset = async (device) => {
    setOpentResetDialog(false);
    if (device) {
      try {
        const response = await fetch(`/api/devices/${device.id}/reset`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        });
        if (response.ok) {
          setSuccessMessage(t('deviceReset'));
          handleRefresh();
          onClose();
        } else {
          const errorText = await response.text();
          try {
            const errorData = JSON.parse(errorText);
            setErrorMessage(errorData.error || errorData.message || response.statusText);
          } catch {
            setErrorMessage(response.statusText);
          }
        }
      } catch (error) {
        setErrorMessage(error.message);
      }
    }
  };

  const placeGeofence = async (latitude, longitude, name, radius) => {
    const geofenceItem = {
      name: `${name}`,
      area: `CIRCLE (${latitude} ${longitude}, ${radius})`,
    };
    const geofence = await postJsonAPI('geofences', geofenceItem, true, null, (msg) => setActionMessage(msg, true));
    if (geofence) {
      await postJsonAPI('permissions', { deviceId, geofenceId: geofence.id }, false, t('sharedGeofenceCreated'), (msg) => setActionMessage(msg, false));
      await createNotification(getNotificationObject('geofenceExit', geofence.id), deviceId);
      await createNotification(getNotificationObject('geofenceEnter', geofence.id), deviceId);
    }
  };

  const handleGeofence = async (item) => {
    if (item.address !== '') {
      const query = new URLSearchParams({ address: item.address });
      const response = await fetch(`/api/server/geodecode?${query.toString()}`);
      if (response.ok) {
        const location = await response.json();
        placeGeofence(location.latitude, location.longitude, item.name, item.radius);
      } else {
        throw Error(await response.text());
      }
    } else {
      placeGeofence(position.latitude, position.longitude, item.name, item.radius);
    }
  };

  const handleGeofenceClose = (item) => {
    setOpenGeofenceDialog(false);
    if (item) {
      handleGeofence(item);
    }
  };

  const handleGeofenceListClose = async (items) => {
    setOpenGeofenceListDialog(false);
    if (items) {
      const results = [];
      items.forEach((item) => {
        if (item.toDelete === true) {
          results.push(fetch(`/api/geofences/${item.id}`, { method: 'DELETE' }));
        }
      });
      if (results.length > 0) {
        await Promise.all(results);
        handleRefresh();
        setActionMessage(t('sharedGeofencesRemoved'), false);
      }
    }
  };

  const handleCommand = async (name) => {
    const id = await getUserCommandId(name, userId);
    if (id) {
      await postJsonAPI('commands/send', { id, deviceId }, false, t('commandSent'), (msg) => setActionMessage(msg, false));
    }
  };

  const handleSendCommand = async (commandId) => {
    await postJsonAPI('commands/send', { id: commandId, deviceId }, false, t('commandSent'), (msg) => setActionMessage(msg, false));
    setAnchorCommands(null);
  };

  const handleSpeedLimitClose = async (item) => {
    setOpenSpeedLimitDialog(false);
    if (item) {
      handleCommand(item.speed);
    }
  };

  const handlePropsClose = async () => {
    setOpenPropsDialog(false);
  };

  const handleImpoundLotClose = async () => {
    setOpenLotInfoDialog(false);
  };

  const handleAnalysisClose = async () => {
    setOpenAnalysisDialog(false);
  };

  const handleRecoveryClose = async (items) => {
    setOpenRecoveryDialog(false);
    if (items) {
      let addedCount = 0;
      let removedCount = 0;

      const results = [];
      items.forEach((item) => {
        if (item.newAssigned !== item.origAssigned) {
          if (item.newAssigned) {
            // Assign device to recovery user
            results.push(fetch(`/api/devices/${deviceId}/recovery/${item.id}`, {
              method: 'PUT',
            }).then((response) => {
              if (!response.ok) {
                throw new Error(`Failed to assign recovery: ${response.statusText}`);
              }
              return response;
            }));
            addedCount += 1;
          } else {
            // Unassign device from recovery user
            results.push(fetch(`/api/devices/${deviceId}/recovery/${item.id}`, {
              method: 'DELETE',
            }).then((response) => {
              if (!response.ok) {
                throw new Error(`Failed to unassign recovery: ${response.statusText}`);
              }
              return response;
            }));
            removedCount += 1;
          }
        }
      });

      if (results.length > 0) {
        try {
          await Promise.all(results);

          if (addedCount > 0 && removedCount > 0) {
            setActionMessage(t('sharedRecoveryUpdated'), false);
          } else if (addedCount > 0) {
            setActionMessage(t('sharedRecoveryAssigned'), false);
          } else if (removedCount > 0) {
            setActionMessage(t('sharedRecoveryUnassigned'), false);
          }

          handleRefresh();
        } catch (error) {
          setActionMessage(error.message, true);
        }
      }
    }
  };

  const handleMaintenanceClose = async (item) => {
    setOpenMaintenanceDialog(false);
    if (item) {
      const maintenance = await postJsonAPI('maintenance', item, true, null);
      if (maintenance) {
        await postJsonAPI('permissions', { deviceId, maintenanceId: maintenance.id }, false, 'Maintenance Record Added', (msg) => setActionMessage(msg, false));
      }
    }
  };

  const handleTransferClose = async (item) => {
    setOpenTransferDialog(false);
    if (item) {
      try {
        const response = await fetch('/api/orders', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            uniqueId: Date.now(),
            description: 'Device Transfer',
            fromAddress: userEmail,
            toAddress: item.email,
            attributes: { deviceId, notify: item.notify },
          }),
        });
        if (response.ok) {
          setSuccessMessage(t('deviceTransferred'));
          handleRefresh();
          onClose();
        } else {
          const errorText = await response.text();
          try {
            const errorData = JSON.parse(errorText);
            setErrorMessage(errorData.error || errorData.message || response.statusText);
          } catch {
            setErrorMessage(response.statusText);
          }
        }
      } catch (error) {
        setErrorMessage(error.message);
      }
    }
  };

  const handleNotificationsClose = async (notifications) => {
    setOpenNotificationsDialog(false);
    if (notifications) {
      let addedCount = 0;
      let removedCount = 0;
      let failedCount = 0;

      const promises = notifications.map(async (notification) => {
        if (notification.newAssigned !== notification.origAssigned) {
          if (notification.newAssigned) {
            const result = await createNotification(notification, deviceId);
            if (result) {
              addedCount += 1;
            } else {
              failedCount += 1;
            }
          } else {
            await deleteNotification(notification.id);
            removedCount += 1;
          }
        }
      });

      await Promise.all(promises);

      // Only show success message if there were no failures
      if (failedCount === 0) {
        if (addedCount > 0 && removedCount > 0) {
          setActionMessage(t('sharedNotificationsUpdated'), false);
        } else if (addedCount > 0) {
          setActionMessage(t('sharedNotificationsAdded'), false);
        } else if (removedCount > 0) {
          setActionMessage(t('sharedNotificationsRemoved'), false);
        }
      }

      handleRefresh();
    }
  };

  const retVal = (
    <div className={classes.root}>
      {device && (
        <Draggable
          handle={`.${classes.media}, .${classes.header}`}
        >
          <Card elevation={3} className={classes.card}>
            <div className={classes.header}>
              <div className={`${classes.headerLeft} ${classes.statusIconsContainer}`}>
                {position && (
                  <StandardProps status={device?.status} position={position} device={device} />
                )}
              </div>
              <div className={classes.headerMiddle}>
                <CopyableText text={device?.name || ''} />
                {(admin || recovery) && (
                  <Typography
                    color="textSecondary"
                    style={{ marginLeft: '8px' }}
                  >
                    {group ? group.name : (device?.groupId ? t('sharedGroupUnknown') : '')}
                  </Typography>
                )}
                {agentDistance && (
                  <Typography color="primary" style={{ marginLeft: '8px', whiteSpace: 'nowrap' }}>
                    {`${agentDistance} ${t('liveTrackAway')}`}
                  </Typography>
                )}
              </div>
              <div className={classes.headerRight}>
                <IconButton
                  size="small"
                  onClick={onClose}
                  onTouchStart={onClose}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>
            </div>
            <CardContent className={classes.content}>
              <TabPanel
                largeScreen={largeScreen}
                device={device}
                position={position}
              />
            </CardContent>
            <CardActions disableSpacing>
              <ActionIcon
                text={t('sharedMaps')}
                color="secondary"
                action={(e) => setAnchorMap(e.currentTarget)}
                icon={(<MapIcon />)}
                disabled={!position}
                largeScreen={largeScreen}
              />
              {canLiveTrack && (
                <ActionIcon
                  text={t('liveTrack')}
                  color={liveTrack.active ? 'primary' : 'secondary'}
                  action={() => (liveTrack.active ? liveTrack.stop() : liveTrack.start())}
                  icon={(
                    <Badge
                      color="primary"
                      badgeContent={liveTrack.active ? `${Math.ceil(liveTrack.remainingMs / 60000)}m` : 0}
                    >
                      <SensorsIcon />
                    </Badge>
                  )}
                  largeScreen={largeScreen}
                />
              )}
              {!largeScreen && (
                <ActionIcon
                  text={t('sharedShowDetails')}
                  color="secondary"
                  action={(e) => setAnchorMore(e.currentTarget)}
                  icon={(<OfflineBoltIcon />)}
                  largeScreen={largeScreen}
                />
              )}
              {canManageAlerts && (
                <ActionIcon
                  text={t('sharedAlerts')}
                  color={lotStatus === 'impound' ? 'negative' : 'secondary'}
                  action={(e) => setAnchorAlerts(e.currentTarget)}
                  icon={(<CircleNotificationsIcon />)}
                  largeScreen={largeScreen}
                />
              )}
              {largeScreen && (
                <>
                  {admin && (
                    <ActionIcon
                      text={t('sharedActions')}
                      color="secondary"
                      action={(e) => setAnchorActions(e.currentTarget)}
                      icon={(<BuildIcon />)}
                      largeScreen={largeScreen}
                    />
                  )}
                  {position && (
                    <ActionIcon
                      text={t('sharedInfoTitle')}
                      color="secondary"
                      action={() => { setOpenPropsDialog(true); }}
                      icon={(<TroubleshootIcon />)}
                      largeScreen={largeScreen}
                    />
                  )}
                  <ActionIcon
                    text={t('reportAIAnalysis')}
                    color="secondary"
                    action={() => { setOpenAnalysisDialog(true); }}
                    icon={(<FaceRetouchingNaturalIcon />)}
                    largeScreen={largeScreen}
                  />
                  <ActionIcon
                    text={t('reportRecentStops')}
                    color="secondary"
                    action={() => { navigate('/reports/stop?autoRun=true'); }}
                    icon={(<StopCircleIcon />)}
                    largeScreen={largeScreen}
                  />
                  {canSendCommands && deviceCommands.length === 1 && (
                    <ActionIcon
                      text={deviceCommands[0].description}
                      color="secondary"
                      action={() => { handleSendCommand(deviceCommands[0].id); }}
                      icon={(<MyLocationIcon />)}
                      largeScreen={largeScreen}
                    />
                  )}
                  {canSendCommands && deviceCommands.length > 1 && (
                    <ActionIcon
                      text={t('sharedCommands')}
                      color="secondary"
                      action={(e) => setAnchorCommands(e.currentTarget)}
                      icon={(<MyLocationIcon />)}
                      largeScreen={largeScreen}
                    />
                  )}
                </>
              )}
              {largeScreen && canAssignRecovery && (
                <ActionIcon
                  text={t('sharedRecovery')}
                  color={device?.recovery ? 'negative' : 'secondary'}
                  action={() => { setOpenRecoveryDialog(true); }}
                  icon={(<RecoveryIcon />)}
                  disabled={!canEditDevice}
                  largeScreen={largeScreen}
                />
              )}
              {!canEditDevice && (
                <ActionIcon
                  text={t('sharedView')}
                  color="primary"
                  action={() => { navigate(`/settings/device/${deviceId}`); }}
                  icon={(<ViewIcon />)}
                  largeScreen={largeScreen}
                />
              )}
              {canEditDevice && (
                <ActionIcon
                  text={t('sharedDevice')}
                  color="primary"
                  action={(e) => setAnchorDevice(e.currentTarget)}
                  icon={(<SmartphoneIcon />)}
                  largeScreen={largeScreen}
                />
              )}
              <MessageDisplay message={actionResult} setMessage={setActionResult} severity={actionResultError ? 'error' : 'success'} />
            </CardActions>
          </Card>
        </Draggable>
      )}
      {position && (
        <Menu
          anchorEl={anchorMap}
          open={Boolean(anchorMap)}
          onClose={() => setAnchorMap(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          {(admin && (
            <MenuItem onClick={() => navigate(`/position/${position.id}`)}>
              <ListItemText>{t('sharedShowDetails')}</ListItemText>
            </MenuItem>
          ))}
          <MapMenuItem latitude={position.latitude} longitude={position.longitude} type="google" />
          <MapMenuItem latitude={position.latitude} longitude={position.longitude} type="bing" />
          <MapMenuItem latitude={position.latitude} longitude={position.longitude} type="street" />
        </Menu>
      )}
      <Menu
        anchorEl={anchorDevice}
        open={Boolean(anchorDevice)}
        onClose={() => setAnchorDevice(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <MenuItem onClick={() => navigate(`/settings/device/${deviceId}`)}>
          <ListItemIcon>
            <EditIcon />
          </ListItemIcon>
          <ListItemText>{t('sharedEdit')}</ListItemText>
        </MenuItem>
        {canManageDevices && (
          <MenuItem onClick={() => setOpenTransferDialog(true)}>
            <ListItemIcon>
              <SendIcon />
            </ListItemIcon>
            <ListItemText>{t('sharedTransfer')}</ListItemText>
          </MenuItem>
        )}
        {device && !device.disabled && canManageDevices && (
          <MenuItem onClick={() => setOpentTerminateDialog(true)}>
            <ListItemIcon className={classes.negative}>
              <DeleteIcon />
            </ListItemIcon>
            <ListItemText className={classes.negative}>{t('sharedTerminate')}</ListItemText>
          </MenuItem>
        )}
        {device && !device.disabled && canManageDevices && (
          <MenuItem onClick={() => setOpentResetDialog(true)}>
            <ListItemIcon className={classes.negative}>
              <DeleteIcon />
            </ListItemIcon>
            <ListItemText className={classes.negative}>{t('sharedReset')}</ListItemText>
          </MenuItem>
        )}
        {admin && (
          <MenuItem onClick={() => setOpenRemoveDialog(true)}>
            <ListItemIcon className={classes.negative}>
              <DeleteIcon />
            </ListItemIcon>
            <ListItemText className={classes.negative}>{t('sharedRemove')}</ListItemText>
          </MenuItem>
        )}
      </Menu>
      <Menu
        anchorEl={anchorActions}
        open={Boolean(anchorActions)}
        onClose={() => setAnchorActions(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {canSendCommands && admin && adminCommands.length > 0 && (
          <MenuItem onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            setAdminCommandsPosition({ top: rect.top, left: rect.right });
            setAnchorActions(null);
            setAnchorAdminCommands(true);
          }}
          >
            <ListItemIcon>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText>{t('deviceCommand')}</ListItemText>
          </MenuItem>
        )}
        {position && (
          <MenuItem onClick={() => setOpenSpeedLimitDialog(true)}>
            <ListItemIcon>
              <SpeedIcon />
            </ListItemIcon>
            <ListItemText>{t('attributeSpeedLimit')}</ListItemText>
          </MenuItem>
        )}
        {position && (
          <MenuItem onClick={() => setOpenMaintenanceDialog(true)}>
            <ListItemIcon>
              <ConstructionOutlinedIcon />
            </ListItemIcon>
            <ListItemText>{t('sharedMaintenance')}</ListItemText>
          </MenuItem>
        )}
      </Menu>
      <Menu
        anchorEl={anchorAlerts}
        open={Boolean(anchorAlerts)}
        onClose={() => setAnchorAlerts(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {lotDetails && (
          <MenuItem onClick={() => setOpenLotInfoDialog(true)}>
            <ListItemIcon className={classes[lotDetails.severity]}>
              <LotIcon />
            </ListItemIcon>
            <ListItemText className={classes[lotDetails.severity]}>{t(lotDetails.lotLabel)}</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => setOpenNotificationsDialog(true)}>
          <ListItemIcon>
            <NotificationsActiveIcon />
          </ListItemIcon>
          <ListItemText>{t('sharedNotifications')}</ListItemText>
        </MenuItem>
        {position && (
          <MenuItem onClick={() => setOpenGeofenceDialog(true)}>
            <ListItemIcon>
              <AddGeofenceIcon />
            </ListItemIcon>
            <ListItemText>{t('sharedCreateGeofence')}</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => setOpenGeofenceListDialog(true)}>
          <ListItemIcon>
            <EditGeofencesIcon />
          </ListItemIcon>
          <ListItemText>{t('sharedGeofences')}</ListItemText>
        </MenuItem>
      </Menu>
      <Menu
        anchorEl={anchorMore}
        open={Boolean(anchorMore)}
        onClose={() => setAnchorMore(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {position && (
          <MenuItem onClick={() => setOpenPropsDialog(true)}>
            <ListItemIcon>
              <TroubleshootIcon />
            </ListItemIcon>
            <ListItemText>{t('sharedInfoTitle')}</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => setOpenAnalysisDialog(true)}>
          <ListItemIcon>
            <FaceRetouchingNaturalIcon />
          </ListItemIcon>
          <ListItemText>{t('reportAIAnalysis')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => navigate('/reports/stop?autoRun=true')}>
          <ListItemIcon>
            <StopCircleIcon />
          </ListItemIcon>
          <ListItemText>{t('reportRecentStops')}</ListItemText>
        </MenuItem>
        {canAssignRecovery && (
          <MenuItem onClick={() => setOpenRecoveryDialog(true)}>
            <ListItemIcon>
              <RecoveryIcon />
            </ListItemIcon>
            <ListItemText>{t('sharedRecovery')}</ListItemText>
          </MenuItem>
        )}
        {canSendCommands && deviceCommands.map((command) => (
          <MenuItem key={command.id} onClick={() => { handleSendCommand(command.id); setAnchorMore(null); }}>
            <ListItemIcon>
              <MyLocationIcon />
            </ListItemIcon>
            <ListItemText>{command.description}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
      <Menu
        anchorEl={anchorCommands}
        open={Boolean(anchorCommands)}
        onClose={() => setAnchorCommands(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {deviceCommands.map((command) => (
          <MenuItem key={command.id} onClick={() => handleSendCommand(command.id)}>
            <ListItemIcon>
              <MyLocationIcon />
            </ListItemIcon>
            <ListItemText>{command.description}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
      <Menu
        anchorReference="anchorPosition"
        anchorPosition={adminCommandsPosition}
        open={Boolean(anchorAdminCommands)}
        onClose={() => setAnchorAdminCommands(null)}
      >
        {adminCommands.map((command) => (
          <MenuItem key={command.id} onClick={() => { handleSendCommand(command.id); setAnchorAdminCommands(null); }}>
            <ListItemIcon>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText>{command.description}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
      {openRemoveDialog && (
        <TerminateDialog
          title={t('sharedRemoveConfirm')}
          open={openRemoveDialog}
          device={device}
          handleClose={handleRemove}
        />
      )}
      {opentTerminateDialog && (
        <TerminateDialog
          title={t('sharedTerminateConfirm')}
          open={opentTerminateDialog}
          device={device}
          handleClose={handleTerminate}
          warning={t('sharedTerminateWarning')}
        />
      )}
      {opentResetDialog && (
        <TerminateDialog
          title={t('sharedResetConfirm')}
          open={opentResetDialog}
          device={device}
          handleClose={handleReset}
          warning={t('sharedResetWarning')}
        />
      )}
      {openGeofenceDialog && (
        <GeofenceDialog
          open={openGeofenceDialog}
          handleClose={handleGeofenceClose}
          disabled={disableActions}
        />
      )}
      {openGeofenceListDialog && (
        <GeofenceListDialog
          userId={userId}
          deviceId={deviceId}
          open={openGeofenceListDialog}
          handleClose={handleGeofenceListClose}
          disabled={disableActions}
        />
      )}
      {openNotificationsDialog && (
        <NotificationsDialog
          open={openNotificationsDialog}
          handleClose={handleNotificationsClose}
          deviceId={deviceId}
          disabled={disableActions}
        />
      )}
      {openSpeedLimitDialog && (
        <SpeedLimitDialog
          open={openSpeedLimitDialog}
          handleClose={handleSpeedLimitClose}
          disabled={disableActions}
        />
      )}
      {openMaintenanceDialog && (
        <MaintenanceDialog
          open={openMaintenanceDialog}
          handleClose={handleMaintenanceClose}
          disabled={disableActions}
        />
      )}
      {openTransferDialog && (
        <TransferDialog
          open={openTransferDialog}
          handleClose={handleTransferClose}
          disabled={disableActions}
        />
      )}
      {openRecoveryDialog && (
        <RecoveryDialog
          deviceId={deviceId}
          open={openRecoveryDialog}
          handleClose={handleRecoveryClose}
          disabled={disableActions}
        />
      )}
      {openPropsDialog && (
        <PropsDialog
          device={device}
          position={position}
          open={openPropsDialog}
          handleClose={handlePropsClose}
        />
      )}
      {openLotInfoDialog && (
        <LotInfoDialog
          deviceId={deviceId}
          title={t(lotDetails?.lotLabel || 'sharedImpoundLot')}
          open={openLotInfoDialog}
          handleClose={handleImpoundLotClose}
        />
      )}
      {openAnalysisDialog && (
        <AIAnalysisDialog
          deviceId={deviceId}
          open={openAnalysisDialog}
          handleClose={handleAnalysisClose}
        />
      )}
    </div>
  );
  // const duration = performance.now() - startTime;
  // console.log(`Status Card took ${duration}ms`);
  return retVal;
};

export default StatusCard;
